import React, { useCallback } from 'react';
import { isNil } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import ProgressStep from 'src/components/ProgressStep/ProgressStep';
import AppTitle from 'src/components/Shared/AppTitle/AppTitle';
import FundingSource from 'src/components/FundingSource/FundingSource/FundingSource';
import { dispatchPageLoad } from 'src/redux/utils/analytics-helper';
import {
  CANCEL_TRADE_CONFIRMATION_MODAL,
  CONTRIBUTION_WINDOW_CLOSED_MODAL,
  openModal,
} from 'src/redux/modules/modals/modals.module';
import { setProgress } from 'src/redux/modules/progress';
import { getProgressCurrentStep } from 'src/redux/modules/progress/selectors';
import { scrollElementIntoFullView } from 'src/utils/dom-utils';
import { Step1Text, Step2Text, Step3Text } from './TitlesAndCopy';
import { getSelectedBank } from '../../../redux/modules/bankSelection/bankSelection.selectors';
import { getBankEditMode } from 'src/redux/modules/editModeBank/selectors';
import {
  getEligibleBanks,
  getIsSingleBankWithDisabledStatus,
  getError,
  getIsLoaded,
  getIsLoading,
} from '../../../redux/modules/existingBanks/existingBanks.selectors';
import {
  hasStopTransferFunds,
  getPurchasingFunds,
  getFlattenedPurchasingFunds,
  getShoppingCartTotal,
} from 'src/redux/modules/shoppingCart/shoppingCart.selectors';
import { isFundListNextButtonDisabled } from '../../../utils/buy/buy-steps-utils.js';
import { getUserFundsRaw } from '../../../redux/modules/funds/UserFunds/userFunds.selectors';
import { formatUnixDate } from '../../../utils/dates/date-utility';
import FundList from 'src/components/Buy/BuyFlow/FundList';
import Review from 'src/components/Buy/BuyFlow/Review';
import './appBuySteps.scss';
import {
  generateTradeDatePurchase,
  getPaperless,
} from '../../../utils/submission/submission-utility';
import { getUserAddress } from '../../../redux/modules/user/PersonalInformation/personalInformation.selectors';
import { getPaperlessInfo } from '../../../redux/modules/user/Paperless/paperless.selectors.js';
import RASText from 'src/components/Shared/RASText/RASText';
import APPText from 'src/components/Shared/APPText/APPText';
import { moduleActions } from '../../../redux/modules';
//import { getEligibleTradeDateAgreementsList } from '../../../redux/modules/executePurchase/executePurchase.module';

import { ThunkDispatch } from 'redux-thunk';
export interface AppBuyStepsProps {
  actions?;
  availableFunds?;
  disableSubmitButton?;
  paperlessSetting?;
  submitButtonDisabled?;
  hasRasFundsOnly?;
  hasAppFundsOnly?;
  title?;
}

export const AppBuySteps: React.FC<AppBuyStepsProps> = ({
  actions,
  availableFunds,
  disableSubmitButton,
  paperlessSetting,
  submitButtonDisabled,
  hasRasFundsOnly,
  hasAppFundsOnly,
}) => {
  const dispatch: ThunkDispatch<any, any, any> = useDispatch();
  const onClickScroll = useCallback(
    (step: number, element: string) => {
      dispatch(setProgress(step));
      scrollElementIntoFullView(element, 15);
    },
    [dispatch]
  );

  const currentStep = useSelector(getProgressCurrentStep);
  const selectedBank = useSelector(getSelectedBank);
  const editModeBank = useSelector(getBankEditMode) ?? false;
  const eligibleBanks = useSelector(getEligibleBanks);
  const stopTransfer: boolean = useSelector(hasStopTransferFunds);
  const error = useSelector(getError);
  const isLoaded = useSelector(getIsLoaded);
  const isLoading = useSelector(getIsLoading);
  const isSingleBankWithDisabledStatus = useSelector(
    getIsSingleBankWithDisabledStatus
  );
  const purchasingFunds = useSelector(getPurchasingFunds);
  const flattenedPurchasingFunds = useSelector(getFlattenedPurchasingFunds);
  const userFunds = useSelector(getUserFundsRaw);
  const firstFund = userFunds[0];
  const shoppingCartTotal = useSelector(getShoppingCartTotal);
  const displayTradeDate = firstFund
    ? formatUnixDate(firstFund.displayTradeDate)
    : '';
  const primaryAddress = useSelector(getUserAddress);
  const paperlessInfo = useSelector(getPaperlessInfo);
  const { eligibleTradeDatesLoading } = useSelector(
    //@ts-ignore
    (state) => state.executePurchase
  );

  function onBuySubmit() {
    const { getContributionWindow, submitPurchase, resetTransaction } = actions;
    //@ts-ignore
    const { paperless, updatedPaperless } = paperlessInfo;
    const { state = null } = primaryAddress || {};
    disableSubmitButton();
    const oldPaperless = getPaperless(paperless, 'MUTUAL_FUND');
    const doSubmit = () =>
      submitPurchase(
        availableFunds,
        updatedPaperless,
        oldPaperless,
        purchasingFunds,
        selectedBank
      );
    scrollElementIntoFullView('buy-page-container');
    //@ts-ignore
    if (purchasingFunds?.some((fund) => fund.priorYearAmount > 0)) {
      //
      // we have prior year contributions so we need to double check again that we didn't cross
      // the prior year threshold as the client was filling out the buy form.
      //
      getContributionWindow(state).then(
        (data) =>
          data.action.payload.body.withinPriorYearContributionPeriod
            ? doSubmit()
            : openModal(CONTRIBUTION_WINDOW_CLOSED_MODAL),
        //
        // Discussion with business concludes that if the call to re-check the contribution
        // window fails, go ahead and do the submit
        //
        doSubmit
      );
    } else {
      doSubmit();
    }
    resetTransaction();
  }
  function handleSubmitTradeDates() {
    const { paperless, updatedPaperless } = paperlessSetting;
    const oldPaperless = getPaperless(paperless, 'MUTUAL_FUND');
    const payload = generateTradeDatePurchase(
      updatedPaperless,
      oldPaperless,
      //@ts-ignore
      purchasingFunds,
      selectedBank
    );
    Promise.all([
      dispatch(moduleActions.getEligibleTradeDateAgreementsList(payload)),
    ]).then(() => {
      onClickScroll(3, 'review-step');
    });
  }

  return (
    <form className="animated fadeInUp" onSubmit={(e) => e.preventDefault()}>
      {hasRasFundsOnly && (
        <AppTitle
          mboxSubtitle="ret-aa-new-app-buy-subtitle"
          mboxTitle="ret-aa-new-app-buy-title"
          subtitle={
            <span>
              Our three-step process makes it easy to add to an existing
              Retirement Advisory Service account(s).
            </span>
          }
          title={
            <span>
              Add to <RASText tradeMark />
            </span>
          }
        />
      )}
      {hasAppFundsOnly && (
        <AppTitle
          mboxSubtitle="ret-aa-new-app-buy-subtitle"
          mboxTitle="ret-aa-new-app-buy-title"
          subtitle={
            <span>
              Our three-step process makes it easy to add to an existing{' '}
              <APPText registeredTradeMark /> account(s).
            </span>
          }
          title={
            <span>
              Add to <APPText registeredTradeMark />
            </span>
          }
        />
      )}
      {!hasRasFundsOnly && !hasAppFundsOnly && (
        <AppTitle
          mboxSubtitle="ret-aa-new-app-buy-subtitle"
          mboxTitle="ret-aa-new-app-buy-title"
          subtitle={
            <span>
              Our three-step process makes it easy to add to an existing{' '}
              <APPText registeredTradeMark /> and/or <RASText tradeMark />{' '}
              account(s).
            </span>
          }
          title={<span> Add to Advisory Accounts </span>}
        />
      )}

      {/* STEP 1 */}
      <ProgressStep
        expanded
        showCancelLink
        active={currentStep === 1}
        className="funding-source-step"
        clickable={currentStep > 1}
        complete={currentStep > 1}
        nextButtonDisabled={
          isNil(selectedBank) || isSingleBankWithDisabledStatus
        }
        nextButtonLabel={<Step1Text.Button.Next />}
        step={1}
        title={<Step1Text.Title />}
        onCancelClick={() =>
          dispatch(openModal(CANCEL_TRADE_CONFIRMATION_MODAL))
        }
        onClickHeader={() => {
          currentStep > 1 && onClickScroll(1, 'funding-source-step');
        }}
        onNextClick={() => {
          onClickScroll(2, 'funding-target-step');
        }}
      >
        <FundingSource
          actions={actions}
          active={currentStep === 1}
          completed={currentStep > 1}
          dispatchPageLoad={dispatchPageLoad}
          editModeBank={{ editModeFlag: editModeBank }}
          error={error}
          fundingSource={eligibleBanks}
          hasRasFundsOnly={hasRasFundsOnly}
          isLoaded={isLoaded}
          isLoading={isLoading}
          openDrawer={() => {
            onClickScroll(1, 'funding-source-step');
          }}
          selectedBank={selectedBank}
          stopTransfer={stopTransfer}
        />
      </ProgressStep>

      {/* STEP 2 */}
      <ProgressStep
        expanded
        showCancelLink
        active={currentStep === 2}
        className="fund-list-step"
        clickable={currentStep >= 2}
        complete={currentStep > 2}
        nextButtonClassName="e2e-step2-verify-submit"
        nextButtonDisabled={isFundListNextButtonDisabled(purchasingFunds)}
        nextButtonLabel={<Step2Text.Button.Next />}
        nextButtonLoading={eligibleTradeDatesLoading}
        step={2}
        title={<Step2Text.Title />}
        onCancelClick={() =>
          dispatch(openModal(CANCEL_TRADE_CONFIRMATION_MODAL))
        }
        onClickHeader={() => {
          currentStep >= 2 && onClickScroll(2, 'funding-target-step');
        }}
        onNextClick={() => {
          handleSubmitTradeDates();
        }}
      >
        <FundList
          clickable={!isFundListNextButtonDisabled(purchasingFunds)}
          displayTradeDate={displayTradeDate}
        />
      </ProgressStep>

      {/* STEP 3 */}
      <ProgressStep
        showCancelLink
        active={currentStep === 3}
        className="review-step"
        clickable={currentStep >= 3}
        complete={currentStep > 3}
        expanded={currentStep >= 3}
        nextButtonDisabled={submitButtonDisabled}
        nextButtonLabel={<Step3Text.Button.Next />}
        step={3}
        title={<Step3Text.Title />}
        onCancelClick={() =>
          dispatch(openModal(CANCEL_TRADE_CONFIRMATION_MODAL))
        }
        onClickHeader={() => {
          currentStep >= 3 && onClickScroll(3, 'review-step');
        }}
        onNextClick={() => {
          onBuySubmit();
        }}
      >
        <Review
          actions={actions}
          dispatchPageLoad={dispatchPageLoad}
          displayTradeDate={displayTradeDate}
          flattenedPurchasingFunds={flattenedPurchasingFunds}
          paperlessSetting={{}}
          selectedBank={selectedBank}
          shoppingCartTotal={shoppingCartTotal}
          userFunds={userFunds}
        />
      </ProgressStep>
    </form>
  );
};
